import { useEffect, useState } from 'react'
import type { FieldError } from '../../../../../hooks/use-fetch'
import { AddSupportCost, UpdateSupportCost } from '../../../../../services/AnnualPlanService'
import { toast } from 'react-toastify'
import { Alert, Box, InputAdornment, TextField, Typography } from '@mui/material'
import { Modal } from '../../../../customComponents/Modal'
import type { AddOrEditAnnualPlanSupportCostRequest } from '../../../../../models/requestObjects/AnnualPlanRequestObjects'
import { type SupportCost } from '../../../../../models/SupportCost'
import { BudgetThresholdExceededModal } from '../../BudgetExceededModal'
import { useConfirmState } from '../../../../../hooks/use-budgetThresholdExceeded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

interface Props {
  orgId: number
  annualPlanId: number
  supportCost?: SupportCost
  isImplementationPhase?: boolean
  onClose: () => void
  onSave: () => void
}

interface SupportCostForm {
  description: string
  budgetRequest?: number
}

export function AddSupportCostModal (props: Props): JSX.Element {
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([])
  const [formValues, setFormValues] = useState<SupportCostForm>({
    description: props.supportCost?.description ?? '',
    budgetRequest: props.supportCost?.adjustedBudget != null ? props.supportCost.adjustedBudget : props.supportCost?.budgetRequest
  })
  const { confirmState, setConfirmState, overBudgetThreshold } = useConfirmState(props.supportCost?.budgetRequest, formValues.budgetRequest)
  const [saving, setSaving] = useState<boolean>(false)
  const isImplementationPhase = props.isImplementationPhase ?? false

  useEffect(() => {
    if (confirmState === 'confirmed') {
      void handleSave()
    }
  }, [confirmState])

  const handleSave = async (): Promise<void> => {
    if (!validateForm() || saving) {
      return
    }
    setSaving(true)

    const request: AddOrEditAnnualPlanSupportCostRequest = {
      annualPlanId: props.annualPlanId,
      orgId: props.orgId,
      description: formValues.description,
      budgetRequest: formValues.budgetRequest?.toString().replace('$', '') ?? '',
      id: props.supportCost?.id,
      isImplementationPhase,
      isUpdate: props.supportCost?.isUpdate,
      needsReview: overBudgetThreshold
    }

    if (props.supportCost != null) {
      if (overBudgetThreshold && isImplementationPhase && confirmState !== 'confirmed') {
        setSaving(false)
        setConfirmState('needsConfirmation')
        return
      }
      await saveEditedCost(request)
    } else {
      await saveAddCost(request)
    }

    setSaving(false)
  }

  async function saveAddCost (request: AddOrEditAnnualPlanSupportCostRequest): Promise<void> {
    const { success, errors } = await AddSupportCost(request)
    if (!success) {
      errors.forEach((error) => {
        toast.error(error)
      })
      return
    }
    props.onSave()
  }

  async function saveEditedCost (request: AddOrEditAnnualPlanSupportCostRequest): Promise<void> {
    const { success, errors } = await UpdateSupportCost(request)
    if (!success) {
      errors.forEach((error) => {
        toast.error(error)
      })
      return
    }
    props.onSave()
  }

  function validateForm (): boolean {
    const formFieldErrors: FieldError[] = []

    if (formValues.description === '') {
      formFieldErrors.push({ fieldName: 'description', message: '' })
    }

    if (formValues.budgetRequest == null || isNaN(formValues.budgetRequest) || Number(formValues.budgetRequest) < 0) {
      formFieldErrors.push({ fieldName: 'budgetRequest', message: 'Budget must be a number greater than 0' })
    }

    setFieldErrors(formFieldErrors)
    if (formFieldErrors.length > 0) {
      toast.error('Please complete all required fields.')
      formFieldErrors.forEach((error) => {
        if (error.message !== '') {
          toast.error(error.message)
        }
      })
      return false
    }

    return true
  }

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value
    })

    setFieldErrors(fieldErrors.filter(f => f.fieldName !== e.target.name))
  }

  const content = <Box sx={{ width: '350px' }}>
    {
      props.isImplementationPhase === true && props.supportCost == null &&
        <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
          backgroundColor: 'var(--blue-500)',
          color: 'white',
          mb: '.5em',
          fill: 'white'
        }} severity="info"
        >The addition of this item will prompt a review from the START team.
        </Alert>
    }
    <Typography variant='h5' sx={{ mb: '1em' }}>Item Info</Typography>
    <TextField
      variant='filled'
      data-testid='description'
      fullWidth
      value={formValues.description}
      onChange={handleFieldUpdate}
      label={'Description'}
      name={'description'}
      multiline={true}
      rows={4}
      required
      error={fieldErrors.some((error) => error.fieldName === 'description')}
    />

    <Typography variant='body1' sx={{ my: '1em' }}>What additional funding does this require?</Typography>
    <TextField
      sx={{ width: '250px' }}
      variant='filled'
      onChange={handleFieldUpdate}
      label={'Budget Request'}
      data-testid='budgetRequest'
      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
      name={'budgetRequest'}
      required
      error={fieldErrors.some((error) => error.fieldName === 'budgetRequest')}
      value={formValues.budgetRequest}
    />

  </Box>

  const getTitle = (): string => {
    if (props.supportCost != null) {
      return isImplementationPhase ? 'Update RCN Support Cost' : 'Edit RCN Support Cost'
    }
    return 'Add RCN Support Costs'
  }

  return (confirmState === 'needsConfirmation'
    ? <BudgetThresholdExceededModal
        onClose={() => {
          setConfirmState('none')
          props.onClose()
        }}
        onConfirm={() => {
          setConfirmState('confirmed')
        }}
    />
    : <Modal
        open={true}
        maxWidth='md'
        title={getTitle()}
        confirmButtonText='Save'
        bodyContent={content}
        onClose={props.onClose}
        onConfirm={handleSave}
    />
  )
}
